import React, { useEffect, useState } from "react";
import {
  Box, TextField, Button, Typography, CircularProgress, Paper, Grid, Alert,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";

const toNum = (v) => (v === "" || v == null ? null : Number(v));
const isDateOk = (ini, fim) => !ini || !fim || new Date(ini) <= new Date(fim);

const AddCouponForm = ({ couponData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const [form, setForm] = useState({
    ...couponData,
    codigo: couponData?.codigo || "",
    descricao: couponData?.descricao || "",
    tipo: couponData?.tipo || "percentual", // "percentual" | "valor"
    valor: couponData?.valor ?? "",
    pedido_minimo: couponData?.pedido_minimo ?? "",
    validade_inicio: couponData?.validade_inicio || "",
    validade_fim: couponData?.validade_fim || "",
    limite_usos_total: couponData?.limite_usos_total ?? "",
    limite_usos_por_cliente: couponData?.limite_usos_por_cliente ?? "",
    status: couponData?.status == null ? 1 : Number(couponData.status),
    id_usuario: couponData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      ...couponData,
      codigo: couponData?.codigo || "",
      descricao: couponData?.descricao || "",
      tipo: couponData?.tipo || "percentual",
      valor: couponData?.valor ?? "",
      pedido_minimo: couponData?.pedido_minimo ?? "",
      validade_inicio: couponData?.validade_inicio || "",
      validade_fim: couponData?.validade_fim || "",
      limite_usos_total: couponData?.limite_usos_total ?? "",
      limite_usos_por_cliente: couponData?.limite_usos_por_cliente ?? "",
      status: couponData?.status == null ? 1 : Number(couponData.status),
      id_usuario: couponData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponData?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    // validações simples
    if (!form.codigo?.trim()) {
      setErrorMessage("Informe o código do cupom.");
      return;
    }
    if (form.tipo === "percentual" && (toNum(form.valor) == null || toNum(form.valor) <= 0 || toNum(form.valor) > 100)) {
      setErrorMessage("Percentual inválido. Use entre 0 e 100.");
      return;
    }
    if (form.tipo === "valor" && (toNum(form.valor) == null || toNum(form.valor) <= 0)) {
      setErrorMessage("Valor do desconto deve ser maior que zero.");
      return;
    }
    if (!isDateOk(form.validade_inicio, form.validade_fim)) {
      setErrorMessage("A data inicial deve ser menor ou igual à final.");
      return;
    }

    const payload = {
      id: form.id,
      codigo: form.codigo.trim(),
      descricao: form.descricao || "",
      tipo: form.tipo, // "percentual" | "valor"
      valor: toNum(form.valor),
      pedido_minimo: toNum(form.pedido_minimo),
      validade_inicio: form.validade_inicio || null,
      validade_fim: form.validade_fim || null,
      limite_usos_total: toNum(form.limite_usos_total),
      limite_usos_por_cliente: toNum(form.limite_usos_por_cliente),
      status: Number(form.status) === 1 ? 1 : 0,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Erro ao salvar o cupom.");
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Cupom" : "Adicionar Cupom"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Código *"
              name="codigo"
              value={form.codigo}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Desconto</InputLabel>
              <Select
                label="Tipo de Desconto"
                name="tipo"
                value={form.tipo}
                onChange={handleChange}
              >
                <MenuItem value="percentual">Percentual (%)</MenuItem>
                <MenuItem value="valor">Valor (R$)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={form.tipo === "percentual" ? "Valor (%) *" : "Valor (R$) *"}
              name="valor"
              type="number"
              inputProps={{ step: "0.01" }}
              value={form.valor}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              name="descricao"
              value={form.descricao}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Pedido mínimo (R$)"
              name="pedido_minimo"
              type="number"
              inputProps={{ step: "0.01" }}
              value={form.pedido_minimo}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Limite total de usos"
              name="limite_usos_total"
              type="number"
              inputProps={{ step: "1", min: "0" }}
              value={form.limite_usos_total}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Limite por cliente"
              name="limite_usos_por_cliente"
              type="number"
              inputProps={{ step: "1", min: "0" }}
              value={form.limite_usos_por_cliente}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.status) === 1}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                  }
                />
              }
              label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Validade (início)"
              name="validade_inicio"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.validade_inicio}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Validade (fim)"
              name="validade_fim"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.validade_fim}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddCouponForm;
